import Vue from 'vue'
import Vuex from 'vuex'
import pkg from '../package.json'

const { title, endpoint, endpointLocal, endpointStaging } = pkg.config
const env = process.env.VUE_APP_ENV

const src =
  env === 'development' && !endpointStaging
    ? endpointLocal
    : env === 'development' || env === 'staging'
    ? endpointStaging
    : endpoint

Vue.use(Vuex)

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {
    error: null,
    loading: true,
    env: env,
    browser: '',
    touch: false,
    mobile: false,
    phone: false,
    route: {},
    slug: '',
    back: '',
    title: title,
    posts: [],

    announcements: [],
    announcementsLoading: false,
    announcementsError: false,

    isCampus: false,
    isMedia: false,
    isLocation: false,
    isHotspot: false,
    isTour: false,
    isTourIntro: false,

    showPager: false,
    showSidebar: false,
    showCategories: false,
    showTourMap: false,
    showUnibuddy: false,
    showInfoTab: false,

    navOpen: false,
    sidebarOpen: true,
    categoriesOpen: true,

    ytIsPlaying: false,
    videoIsAudioDescribed: false,

    bgVideoIsPlaying: true,
    bgVideoWasPlaying: true
  },

  getters: {
    posts: state => state.posts,
    level: state => state.route.params.level_id,
    post: state => state.posts.find(({ slug }) => slug === state.slug),
    options: state => state.posts.find(({ type }) => type === 'page'),
    categories: state => state.posts.filter(({ type }) => type === 'category'),
    studying: state => state.posts.find(({ slug }) => slug === 'studying'),

    ctas: (state, getters) => {
      if (getters.level) {
        switch (getters.level) {
          case 'ug':
            return getters.options.calls_to_action.ug
          case 'pg':
            return getters.options.calls_to_action.pg
        }
      }
    },

    unibuddy: (state, getters) => {
      const defaultUnibuddy = getters.options.unibuddy_default
      const category = getters.getPostBySlug(state.route.params.category_id)

      const categoryUnibuddyMaybe = category?.unibuddy

      return categoryUnibuddyMaybe ? categoryUnibuddyMaybe : defaultUnibuddy
    },

    getPostBySlug: state => slug =>
      state.posts.find(item => item.slug === slug),

    getTourBySlug: (state, getters) => slug => {
      const tour = state.posts.find(item => item.slug === slug)
      const children = tour.children || []
      const studyLevel = getters.level

      // Tour children filtered by studyLevel if applicable.
      const content = children.filter(
        item => item.study_level === studyLevel || !item.study_level
      )

      return {
        ...tour,
        children: content
      }
    },

    getCategoryBySlug: (state, getters) => slug => {
      const category = state.posts.find(item => item.slug === slug)
      const children = category?.children || []
      const studyLevel = getters.level

      // Category children filtered by studyLevel if applicable.
      const content = children.filter(
        item => item.study_level === studyLevel || !item.study_level
      )

      // Filters the location children.
      const media = content
        .map(item => {
          const children = item.children || []
          return {
            ...item,
            children: children
              .filter(o => o.study_level === studyLevel || !o.study_level)
              .filter(o => !o.parents || o.parents.includes(category.id))
          }
        })
        .filter(item => {
          return item.type === 'location' ? item.children.length : item
        })

      return {
        ...category,
        children: media
      }
    },

    announcements: (state, getters) => {
      const category = getters.getPostBySlug(state.route.params.category_id)
      const studyLevel = getters.level

      const announcements = state.announcements
        .filter(item => {
          // Filter items if between date range
          const now = Date.now()
          const activates = parseInt(
            new Date(item.activation_date.replace(/\s/, 'T')).getTime()
          )
          const deactivates = parseInt(
            new Date(item.deactivation_date.replace(/\s/, 'T')).getTime()
          )

          const active = now >= activates && now <= deactivates

          if (active) {
            return item
          }
        })
        .filter(item => item.study_level === studyLevel || !item.study_level)
        .filter(item => {
          if (category) {
            return !item.parents || item.parents.includes(category.id)
          } else {
            return !item.parents
          }
        })

      const dateOrderdAnnouncements = announcements.sort(
        (a, b) => new Date(a.deactivation_date) - new Date(b.deactivation_date)
      )

      return dateOrderdAnnouncements
    }
  },

  mutations: {
    browser(state, browser) {
      state.browser = browser
    },
    isLoading(state, loading) {
      state.loading = loading
    },
    isTouch(state) {
      state.touch = true
    },
    isMobile(state, mobile) {
      state.mobile = mobile
    },
    isPhone(state, phone) {
      state.phone = phone
    },
    route(state, route) {
      const slug = route.path.split('/').pop() || '/'
      state.slug = slug
      state.route = route
      state.back = route.meta.back
      state.isCampus = route.meta.campus || false
      state.isLocation = route.meta.location || false
      state.isMedia = route.meta.media || false
      state.isHotspot = route.meta.hotspot || false
      state.isTour = route.meta.tour || false
      state.isTourIntro = route.meta.intro || false
      state.showCategories = route.meta.categories || false
      state.showSidebar = route.meta.sidebar || false
      state.showPager = route.meta.pager || false
    },
    toggleNavState(state) {
      state.navOpen = !state.navOpen
    },
    toggleCategoriesState(state, isOpen) {
      if (isOpen === true || isOpen === false) {
        state.categoriesOpen = isOpen
      } else {
        state.categoriesOpen = !state.categoriesOpen
      }
    },
    toggleSidebarState(state, isOpen) {
      if (isOpen === true || isOpen === false) {
        state.sidebarOpen = isOpen
      } else {
        state.sidebarOpen = !state.sidebarOpen
      }
    },
    toggleTourMapState(state, isOpen) {
      if (isOpen === true || isOpen === false) {
        state.showTourMap = isOpen
      } else {
        state.showTourMap = !state.showTourMap
      }
    },
    toggleUnibuddyState(state) {
      state.showUnibuddy = !state.showUnibuddy
    },
    toggleInfoTabState(state) {
      state.showInfoTab = !state.showInfoTab
    },
    toggleAudioDescription(state) {
      state.videoIsAudioDescribed = !state.videoIsAudioDescribed
    },
    toggleYouTubeState(state, playing) {
      state.ytIsPlaying = playing
    },
    toggleBgVideoState(state, playing) {
      state.bgVideoIsPlaying = playing
    },
    toggleBgVideoPrevState(state, wasPlaying) {
      state.bgVideoWasPlaying = wasPlaying
    },
    populate(state, { posts, error } = {}) {
      state.error = error

      const data = posts.map(item => {
        const acf = item.acf

        return {
          title: item.title.rendered,
          type: item.type === 'post' ? 'article' : item.type,
          slug: ['page', 'category', 'location'].includes(item.type)
            ? item.slug
            : `${item.slug}_${item.type}`,
          id: item.id,
          ...acf
        }
      })

      const content = data.filter(({ type }) =>
        ['article', 'gallery'].includes(type)
      )

      const videos = data
        .filter(({ type }) => type === 'video')
        .map(item => {
          const video =
            item.player === 'kaltura'
              ? item.kaltura
              : item.player === 'vimeo'
              ? item.vimeo
              : item.youtube

          return {
            ...item,
            video: video
          }
        })

      const panoramas = data
        .filter(({ type }) => type === 'panorama')
        .map(item => {
          const children = item.children || []

          const hotSpots = children.map(({ entry }) =>
            data.find(o => o.id === entry.ID)
          )

          return {
            ...item,
            children: hotSpots.map((o, i) => ({
              ...o,
              ...item.children[i].position
            }))
          }
        })
      // console.log(panoramas.find(({ slug }) => slug === 'around-george-square'))
      // console.log(panoramas.filter(item => item.children.length))

      const tours = data
        .filter(({ type }) => type === 'tour')
        .map(item => {
          const children = item.children || []
          return {
            ...item,
            children: children
              .map(o => data.find(f => f.id === o))
              .map(o => {
                switch (o.type) {
                  case 'panorama':
                    return panoramas.find(f => f.id === o.id)
                  case 'video':
                    return videos.find(f => f.id === o.id)
                  default:
                    return content.find(f => f.id === o.id)
                }
              })
              .filter(o => o)
          }
        })

      const categoriesAndSubjects = data
        .filter(({ type }) =>
          ['category', 'undergraduate', 'postgraduate'].includes(type)
        )
        .map(item => {
          const children = item.children || []
          const video = item.video
            ? videos.find(o => o.id === item.video)
            : false

          return {
            ...item,
            video: video,
            children: children
              .map(o => data.find(f => f.id === o))
              .filter(o => o)
              .map(x => {
                const innerChildren = x.children || []
                return {
                  ...x,
                  children: innerChildren
                    .map(o => data.find(f => f.id === o))
                    .filter(o => o)
                    .map(o => {
                      switch (o.type) {
                        case 'panorama':
                          return panoramas.find(f => f.id === o.id)
                        case 'video':
                          return videos.find(f => f.id === o.id)
                        default:
                          return content.find(f => f.id === o.id)
                      }
                    })
                    .filter(o => o)
                }
              })
          }
        })

      const subjects = categoriesAndSubjects.filter(({ type }) =>
        ['undergraduate', 'postgraduate'].includes(type)
      )

      const categories = categoriesAndSubjects
        .filter(({ type }) => type === 'category')
        .map(item => {
          if (item.slug === 'studying') {
            return {
              ...item,
              subjects: subjects.sort((a, b) => a.title.localeCompare(b.title))
            }
          }
          return {
            ...item
          }
        })

      // Really just one page is used for site options ctas etc
      const pages = data.filter(({ type }) => type === 'page')

      const announcements = data.filter(({ type }) => type === 'announcement')

      state.announcements = announcements

      state.posts = [
        ...panoramas,
        ...tours,
        ...categories,
        ...subjects,
        ...pages,
        ...content,
        ...videos
      ]
    },

    populateAnnouncements(state, { announcements, error, loading } = {}) {
      state.announcementsLoading = loading
      state.announcementsError = error

      const filteredAnnouncements = announcements.map(item => {
        const acf = item.acf

        return {
          title: item.title.rendered,
          type: item.type,
          slug: `${item.slug}_${item.type}`,
          id: item.id,
          ...acf
        }
      })

      state.announcements = filteredAnnouncements
    }
  },

  actions: {
    fetchAnnouncements({ commit }) {
      commit({
        type: 'populateAnnouncements',
        announcements: [],
        loading: true
      })

      fetch(`${src}/wp/v2/announcements`)
        .then(response => response.json())
        .then(json => {
          if (json.error) {
            throw json
          }
          commit({
            type: 'populateAnnouncements',
            announcements: json,
            loading: false
          })
        })
        .catch(({ error }) => {
          console.log(error)
          commit({
            type: 'populateAnnouncements',
            announcements: [],
            error
          })
        })
    }
  }
})

export default store
